import { getStore, updateStore } from 'core_main/store';
import { KustomerErrorResponse, PublicCallback } from 'globals/types';
import { ErrorMessage, handleError, debug, LogLabel } from 'globals/errors';
import Pubnub from 'pubnub';
import { backoffAuthCall } from 'core_main/api/pubnub';
import { handleCustomerDeletedEvent } from 'core_main/pubnub/events/customerDeleted';
import { handleChatSessionDeletedEvent } from 'core_main/pubnub/events/chatSessionDelete';
import {
  clearPubNub,
  getConversationPubNub,
  getMetaPubNub,
} from './helpers/instance';
import {
  KUSTOMER_APP_CHAT_CSAT_SEND_EVENT,
  KUSTOMER_APP_CHAT_MESSAGE_RECEIVE_EVENT,
  KUSTOMER_APP_CHAT_MESSAGE_SEND_EVENT,
  KUSTOMER_APP_CHAT_SESSION_DELETE_EVENT,
  KUSTOMER_APP_CHAT_SESSION_END_EVENT,
  KUSTOMER_APP_CHAT_SESSION_MERGED_EVENT,
  KUSTOMER_APP_CHAT_SESSION_UPDATE_EVENT,
  KUSTOMER_APP_CHAT_URL_UNFURLED_SEND_EVENT,
  KUSTOMER_APP_CHAT_URL_UNFURLED_RECEIVE_EVENT,
  KUSTOMER_APP_CUSTOMER_DELETED,
  KUSTOMER_ASSISTANT_CHAT_SESSION_END_EVENT,
  PUBNUB_SUBSCRIBE_OPERATION,
  USER_TYPING,
  USER_TYPING_ENDED,
  AI_ASSISTANT_USER_TYPING,
} from './constants';
import { handlePubNubConversationSubscribeOperation } from './events/conversationSubscribeOperation';
import {
  createConversationChannelGroupString,
  createMetaCustomerChannelString,
} from './helpers/channelName';

import { InitializePubNubParameters } from './types';
import { handleChatMessageReceivedEvent } from './events/chatMessageReceived';
// eslint-disable-next-line import/no-cycle
import { handleChatSessionUpdateEvent } from './events/chatSessionUpdate';
import { handleChatSessionMergedEvent } from './events/chatSessionMerged';
import { handleChatSessionEndedEvent } from './events/chatSessionEnded';
import { handleAssistantSessionEndedEvent } from './events/assistantSessionEnded';
import { handleChatCsatSendEvent } from './events/chatCsatSend';
import { handleUserTypingEvent } from './events/userTyping';
import {
  createPubnubNetworkStatusEventHandler,
  setHealthCheckFunction as setNetworkStatusHealthCheck,
} from './events/networkStatus';
import { handleUrlUnfurled } from './events/handleUrlUnfurled';
import {
  setupGrantTokenRefresh,
  teardownGrantTokenRefresh,
  setHealthCheckFunction as setGrantTokenHealthCheck,
} from './helpers/grantToken';
// Health check is lazy-loaded to reduce initial bundle size
// It's only needed when PubNub connection issues occur

export {
  getConversationPubNub,
  getMetaPubNub,
  clearPubNub,
  stopPubNub,
} from './helpers/instance';

/**
 * Lazy-loads and calls the health check wrapper.
 * This reduces initial bundle size by only loading health check code when needed.
 */
export async function checkAndReconnectIfNeededWrapper(): Promise<void> {
  try {
    const { checkAndReconnectIfNeededWrapper: healthCheck } = await import(
      /* webpackChunkName: "pubnub-health-check" */ './helpers/checkAndReconnect'
    );
    await healthCheck();
  } catch (error) {
    handleError(error, {
      message: 'Failed to lazy-load PubNub health check',
      labels: [LogLabel.PUBNUB_RECOVERY],
    });
  }
}

export const createPubNubMetaListeners = (outboundChatEnabled) => ({
  message: (messageEvent: Pubnub.MessageEvent) => {
    const { message } = messageEvent;

    const { name } = message;

    try {
      switch (name) {
        case KUSTOMER_APP_CHAT_SESSION_END_EVENT:
          handleChatSessionEndedEvent(message);
          break;
        case KUSTOMER_ASSISTANT_CHAT_SESSION_END_EVENT:
          handleAssistantSessionEndedEvent(message);
          break;
        case KUSTOMER_APP_CHAT_SESSION_UPDATE_EVENT:
          handleChatSessionUpdateEvent(message);
          break;
        case KUSTOMER_APP_CHAT_SESSION_MERGED_EVENT:
          handleChatSessionMergedEvent(message);
          break;
        case KUSTOMER_APP_CUSTOMER_DELETED:
          handleCustomerDeletedEvent();
          break;
        case KUSTOMER_APP_CHAT_SESSION_DELETE_EVENT:
          handleChatSessionDeletedEvent(message);
          break;
        default:
          break;
      }
    } catch (e) {
      handleError(e);
    }
  },
  signal: (signalEvent) => {
    const { message } = signalEvent;

    switch (message) {
      case USER_TYPING:
      case AI_ASSISTANT_USER_TYPING:
      case USER_TYPING_ENDED: {
        handleUserTypingEvent(signalEvent, {
          isAIGeneratingAnswer: message === AI_ASSISTANT_USER_TYPING,
        });
        break;
      }
      default:
        break;
    }
  },
  status: createPubnubNetworkStatusEventHandler(outboundChatEnabled),
});

export const createPubNubConversationListeners = (
  customerId: string,
  orgId: string,
  cb?: PublicCallback,
) => ({
  message: (messageEvent: Pubnub.MessageEvent) => {
    const { message, timetoken } = messageEvent;

    const { name } = message;

    try {
      switch (name) {
        case KUSTOMER_APP_CHAT_MESSAGE_RECEIVE_EVENT:
          handleChatMessageReceivedEvent(messageEvent);
          break;
        case KUSTOMER_APP_CHAT_MESSAGE_SEND_EVENT:
          handleChatMessageReceivedEvent(messageEvent);
          break;
        case KUSTOMER_APP_CHAT_CSAT_SEND_EVENT:
          handleChatCsatSendEvent(message, timetoken);
          break;
        case KUSTOMER_APP_CHAT_URL_UNFURLED_SEND_EVENT:
        case KUSTOMER_APP_CHAT_URL_UNFURLED_RECEIVE_EVENT:
          handleUrlUnfurled(messageEvent);
          break;
        default:
          break;
      }
    } catch (e) {
      handleError(e);
    }
  },
  status: (statusEvent: Pubnub.StatusEvent) => {
    const { operation } = statusEvent;

    if (operation === PUBNUB_SUBSCRIBE_OPERATION) {
      handlePubNubConversationSubscribeOperation(
        statusEvent,
        orgId,
        customerId,
        cb,
      );
    }
  },
});

/** True when the failed request is Kustomer PubNub auth HTTP 400 (EUX-1702 / EUX-1698). */
function isPubNubAuthHttp400(error: unknown): boolean {
  const e = error as KustomerErrorResponse;
  return (
    e?.status === '400' &&
    typeof e?.url === 'string' &&
    e.url.includes('/pubnub/auth')
  );
}

export const initializePubNub = ({
  customerId,
  orgId,
  cb,
}: InitializePubNubParameters) => {
  const store = getStore();

  const { settings } = store;

  const { outboundChatEnabled } = settings;

  debug({
    message: `initializePubNub called, customerId: ${customerId}, orgId: ${orgId}, isPubNubConnected: ${store.isPubNubConnected}`,
    labels: [LogLabel.PUBNUB_RECOVERY]
  });

  if (!customerId || !orgId || store.isPubNubConnected) {
    debug({
      message: `Early return from initializePubNub, hasCustomerId: ${!!customerId}, hasOrgId: ${!!orgId}, isPubNubConnected: ${store.isPubNubConnected}`,
      labels: [LogLabel.PUBNUB_RECOVERY]
    });
    if (typeof cb === 'function') cb();
    return Promise.resolve();
  }

  teardownGrantTokenRefresh();
  clearPubNub();

  return setupGrantTokenRefresh(outboundChatEnabled)
    .catch(() => {
      // if an unrecoverable error occurs, try v1 auth
      return backoffAuthCall({ version: 1, outboundChatEnabled: false });
    })
    .catch((error: unknown) => {
      // HTTP 400 on `/pubnub/auth` is often expected (org without realtime / misconfig). Degrade instead of
      // rejecting so the widget does not surface an unhandled error; Sentry `http.client` for this case is
      // dropped in `shouldDropPubNubHttpClientNoise` (EUX-1702, EUX-1698).
      if (isPubNubAuthHttp400(error)) {
        console.warn(ErrorMessage.PUBNUB_AUTH_NOT_CONFIGURED, error);
        return undefined;
      }
      return Promise.reject(error);
    })
    .then((authResponse) => {
      debug({ message: `Auth response received: ${authResponse ? 'success' : 'failed'}`, labels: [LogLabel.PUBNUB_RECOVERY] });
      // `undefined` auth: skip PubNub listeners/subscribe (graceful degradation after 400 path above).
      if (!authResponse) {
        debug({ message: 'No auth response, returning early', labels: [LogLabel.PUBNUB_RECOVERY] });
        if (typeof cb === 'function') cb();
        return;
      }

      getMetaPubNub()?.instance?.addListener(
        createPubNubMetaListeners(outboundChatEnabled),
      );

      getConversationPubNub()?.instance?.addListener(
        createPubNubConversationListeners(customerId, orgId, cb),
      );

      updateStore('isPubNubConnected', true);

      const channelGroupString = createConversationChannelGroupString(
        customerId,
        orgId,
      );
      const customerMetaChannel = createMetaCustomerChannelString(
        customerId,
        orgId,
      );

      getConversationPubNub()?.instance?.subscribe({
        channelGroups: [channelGroupString],
      });
      getMetaPubNub()?.instance?.subscribe({
        channels: [customerMetaChannel],
      });

      // Set up dependency injection for health check function
      // This allows grantToken.ts and networkStatus.ts to call the health check
      // without creating circular dependencies
      // Health check is lazy-loaded, so we pass an async wrapper function
      setGrantTokenHealthCheck(async () => {
        await checkAndReconnectIfNeededWrapper();
      });
      setNetworkStatusHealthCheck(async () => {
        await checkAndReconnectIfNeededWrapper();
      });

      // Optional: Check and reconnect if needed as a safeguard (Customer ID/History trigger)
      // Non-blocking call (fire-and-forget) to avoid delaying initialization callback
      // Errors are handled inside checkAndReconnectIfNeededWrapper, no need for additional error handling
      debug({ message: 'About to call checkAndReconnectIfNeededWrapper from pubnub/index.ts', labels: [LogLabel.PUBNUB_RECOVERY] });
      checkAndReconnectIfNeededWrapper().catch(() => {
        // Errors are already handled inside checkAndReconnectIfNeededWrapper
      });
    });
};
